import { Add, FileDownloadOutlined } from '@mui/icons-material';
import { Button, CircularProgress, styled, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PowerPointIcon from '../../../assets/ms_powerpoint.svg';
import { softDeleteQuestion, validateFindingsAnalysis } from '../../../services/dealGPT';
import type {
  PageInfo,
  ProjectRunAnalysisQuestion,
  ProjectWithLastScanned,
  ValidateFindingsOptions,
} from '../../../Types/dealGPT';
import {
  AnalysisQuestionStatus,
  AnalysisQuestionType,
  ChatMode,
  UpdatedReason,
} from '../../../Types/enums';
import { FeedbackInterface } from '../../../Types/feedback';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import ChatIconButton from '../ChatIconButton';
import { getLatestUniqueQuestions, sortQuestions } from '../utils';
import BenchmarkPage from './Benchmark/BenchmarkPage';
import KPIPage from './KPIs/KPIPage';
import PromptAnswer from './PromptAnswer';
import QuestionAnswer from './QuestionAnswer';
import RubricPage from './Rubric/RubricPage';

const StepPageContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const Section = styled('div')({
  '&:not(:first-of-type)': {
    borderTop: '1px solid #E9E9E9',
    paddingTop: '20px',
  },
});

interface StepPageProps {
  page: PageInfo;
  initialPageQuestions?: ProjectRunAnalysisQuestion[];
  pageQuestions?: ProjectRunAnalysisQuestion[];
  latestRunId?: string | null;
  project: ProjectWithLastScanned;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  refetchQuestions: () => void;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  setShouldFetchQuestions: React.Dispatch<React.SetStateAction<boolean>>;
  handleGenerateControlSlide?: (
    slideTitleQuestion?: ProjectRunAnalysisQuestion,
    slideContentQuestion?: ProjectRunAnalysisQuestion
  ) => Promise<void>;
  handleGenerateBenchmarkSlide?: () => Promise<void>;
  isGeneratingPresentation?: boolean;
}

const StepPage: React.FC<StepPageProps> = ({
  page,
  initialPageQuestions,
  pageQuestions,
  latestRunId,
  project,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  refetchQuestions,
  handleFeedbackSubmit,
  setShouldFetchQuestions,
  handleGenerateControlSlide,
  handleGenerateBenchmarkSlide,
  isGeneratingPresentation,
}) => {
  const [responses, setResponses] = useState<ProjectRunAnalysisQuestion[]>([]);
  const [allVersions, setAllVersions] = useState<ProjectRunAnalysisQuestion[]>([]);
  const [highestQuestionIndex, setHighestQuestionIndex] = useState<number>(1);
  const [suggestSaveOrProcessAll, setSuggestSaveOrProcessAll] = useState<'save' | 'process' | null>(
    null
  );
  const [selectedVersions, setSelectedVersions] = useState<{ [key: string]: string }>({});
  const [expandAllKpis, setExpandAllKpis] = useState<boolean>(false);
  const [expandAllRubric, setExpandAllRubric] = useState<boolean>(false);
  const analytics = useAnalytics();

  const handleAddQuestion = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_FR_ADD_QUESTION,
      { id: latestRunId?.toString() },
      {
        projectId: project?.id,
        runId: latestRunId ?? undefined,
        scope: page.scope,
        pageName: page.name,
      }
    );
    if (initialPageQuestions) {
      const newQuestion: ProjectRunAnalysisQuestion =
        page.name === 'Flash Report'
          ? { ...initialPageQuestions[initialPageQuestions.length - 1] }
          : { ...initialPageQuestions[0] };
      newQuestion.displayOrder = highestQuestionIndex + 1;
      newQuestion.updatedReason = UpdatedReason.USER_INPUT;
      setResponses([...responses, newQuestion]);
      setHighestQuestionIndex(highestQuestionIndex + 1);
    }
  }, [
    responses,
    highestQuestionIndex,
    initialPageQuestions,
    analytics,
    latestRunId,
    page.name,
    page.scope,
    project?.id,
  ]);

  const handleDeleteQuestion = async (questionIndex: number) => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_FR_DELETE_QUESTION,
      { id: latestRunId?.toString() },
      {
        projectId: project?.id,
        runId: latestRunId ?? undefined,
        scope: page.scope,
        pageName: page.name,
      }
    );
    const deletedQuestion = responses[questionIndex];

    // There was a bug that allowed to process the initial flash report question, this is to let the user delete it.
    if (
      (latestRunId && deletedQuestion.response) ||
      (deletedQuestion.promptType === 'Key Questions' &&
        !deletedQuestion.userInput &&
        !deletedQuestion.prettyQuestion)
    ) {
      try {
        const isDeleted = await softDeleteQuestion(deletedQuestion);
        if (isDeleted) {
          refetchQuestions();
        }
      } catch (error) {
        enqueueSnackbar('Failed to delete question. Please try again later.', {
          variant: 'error',
        });
        return;
      }
    } else {
      setResponses(responses.filter((_, index) => index !== questionIndex));
    }
  };

  const toggleCollapseExpandAllKpis = () => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_KPI_EXPAND_ALL,
      {
        id: latestRunId?.toString(),
        value: `Scope=${page.scope},Expand=${!expandAllKpis}`,
      },
      {
        projectId: project?.id,
        runId: latestRunId ?? undefined,
        scope: page.scope,
        allCategoriesWereExpanded: expandAllKpis,
        allCategoriesNowExpanded: !expandAllKpis,
      }
    );
    setExpandAllKpis(!expandAllKpis);
  };

  const toggleCollapseExpandAllRubric = () => {
    analytics.event(AnalyticsEvent.WM_IA_A_RUBRIC_EXPAND_ALL, {
      id: latestRunId?.toString(),
      value: `Scope=${page.scope},Expand=${!expandAllRubric}`,
    }),
      {
        projectId: project?.id,
        runId: latestRunId ?? undefined,
        scope: page.scope,
        allCategoriesWereExpanded: expandAllRubric,
        allCategoriesNowExpanded: !expandAllRubric,
      };
    setExpandAllRubric(!expandAllRubric);
  };

  const handleValidateFindingsClick = useCallback(
    async (questionId: string) => {
      setIsLoadingThis(true);
      try {
        const questionObject = pageQuestions?.find((q) => q.id === questionId);

        //Getting Validate Findigs for current question.
        const options: ValidateFindingsOptions = {
          response: questionObject?.response ?? '',
          question: questionObject?.prettyQuestion ?? '',
          scope: page.scope ?? '',
        };
        analytics.event(
          AnalyticsEvent.WM_IA_A_VALIDATE,
          {
            id: questionObject?.id,
            value: `Scope=${page.scope}`,
          },
          {
            projectId: project?.id,
            runId: latestRunId ?? undefined,
            scope: page.scope,
            questionId: questionObject?.id,
            pageName: page.name,
          }
        );
        const responseValidation = questionObject
          ? await validateFindingsAnalysis(options, questionObject.id, project.id)
          : undefined;
        if (responseValidation && responseValidation.id) {
          enqueueSnackbar('Validation complete.', { variant: 'info' });
          setShouldFetchQuestions(true);
        } else {
          enqueueSnackbar(`Failed to validate findings`, {
            variant: 'error',
          });
        }
      } catch (error) {
        enqueueSnackbar('Failed to validate findings', { variant: 'error' });
      } finally {
        setIsLoadingThis(false);
      }
    },
    [
      page.scope,
      project.id,
      pageQuestions,
      analytics,
      setIsLoadingThis,
      setShouldFetchQuestions,
      latestRunId,
      page.name,
    ]
  );

  const processThisPageButton = useMemo(
    () => (
      <Tooltip
        title={
          <div style={{ whiteSpace: 'pre-line' }}>
            {
              "Generate or regenerate an LLM output for every question on this page!\n\nDon't worry, you can go back to a previous version."
            }
          </div>
        }
      >
        <Button
          variant="outlined"
          onClick={async () => {
            analytics.event(
              AnalyticsEvent.WM_IA_A_GENERATE_PAGE,
              {
                id: latestRunId?.toString(),
                value: `Page=${page.name},Scope=${page.scope}`,
              },
              {
                projectId: project?.id,
                runId: latestRunId ?? undefined,
                scope: page.scope,
                pageName: page.name,
              }
            );
            setSuggestSaveOrProcessAll('process');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setSuggestSaveOrProcessAll(null);
          }}
        >
          Generate This Page
        </Button>
      </Tooltip>
    ),
    [setSuggestSaveOrProcessAll, analytics, latestRunId, page.name, page.scope, project.id]
  );

  const handleSelectVersion = useCallback((questionId: string, versionId: string) => {
    setSelectedVersions((prev) => ({
      ...prev,
      [questionId]: versionId,
    }));
  }, []);

  const onHandleGenerateControlPresentation = useCallback(() => {
    if (!handleGenerateControlSlide) return;

    const slideTitleQuestionBase = responses.find((q) => q.promptType === 'Slide Title');
    const slideContentQuestionBase = responses.find((q) => q.promptType === 'Slide Details');

    if (!slideTitleQuestionBase || !slideContentQuestionBase) return;

    const slideTitleVersionId = selectedVersions[slideTitleQuestionBase.id];
    const slideContentVersionId = selectedVersions[slideContentQuestionBase.id];

    const slideTitleQuestion = slideTitleVersionId
      ? allVersions.find((v) => v.id === slideTitleVersionId && v.promptType === 'Slide Title')
      : slideTitleQuestionBase;

    const slideContentQuestion = slideContentVersionId
      ? allVersions.find((v) => v.id === slideContentVersionId && v.promptType === 'Slide Details')
      : slideContentQuestionBase;

    handleGenerateControlSlide(slideTitleQuestion, slideContentQuestion);
  }, [responses, selectedVersions, allVersions, handleGenerateControlSlide]);

  useEffect(() => {
    if (responses.length === 0) {
      handleAddQuestion();
    }
  }, [responses, handleAddQuestion]);

  useEffect(() => {
    // Clear these out when switching pages
    setResponses([]);
    setAllVersions([]);

    if (pageQuestions && pageQuestions.length > 0) {
      setAllVersions(pageQuestions);
      const sortedUniqueQuestions = sortQuestions(getLatestUniqueQuestions(pageQuestions)).filter(
        (q) => q.status !== AnalysisQuestionStatus.INITIAL || q.pageName !== 'Flash Report'
      );
      setResponses(sortedUniqueQuestions);
      if (sortedUniqueQuestions.length > 0) {
        const lastQuestion = sortedUniqueQuestions[sortedUniqueQuestions.length - 1];
        const firstQuestion = sortedUniqueQuestions[0];

        if (
          Number(lastQuestion.displayOrder) &&
          firstQuestion.type === AnalysisQuestionType.QUESTION_AND_ANSWER
        ) {
          setHighestQuestionIndex(Number(lastQuestion.displayOrder));
        }
      }
    }
  }, [pageQuestions, page.name]);

  const slideGenerator = useMemo(() => {
    const { name, scope } = page;

    const generators: Partial<Record<'Slide Details' | 'Benchmark', () => void>> = {
      'Slide Details': onHandleGenerateControlPresentation,
      Benchmark: handleGenerateBenchmarkSlide,
    };

    if (
      name === 'Slide Details' &&
      scope &&
      !['Cybersecurity', 'Corporate Infrastructure'].includes(scope)
    ) {
      return undefined; // Restrict "Slide Details" to Cybersecurity and Corporate Infrastructure scopes
    }

    return generators[name as keyof typeof generators];
  }, [page, onHandleGenerateControlPresentation, handleGenerateBenchmarkSlide]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <StepPageContainer>
        <div style={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
          <Typography variant="h2" sx={{ fontSize: '24px' }}>
            {page.name}
          </Typography>
          <ChatIconButton
            project={project}
            mode={ChatMode.ANALYSIS}
            scope={page.scope}
            pageName={page.name}
          />
          {slideGenerator && (
            <Button
              variant="outlined"
              onClick={slideGenerator}
              disabled={isGeneratingPresentation}
              sx={{
                width: 'auto',
                height: '40px',
                minHeight: '40px',
                gap: '4px',
                border: '1px solid #D35230',
                borderRadius: '2px',
                color: '#D35230',
                '&:hover': {
                  border: '1px solid #D35230',
                  backgroundColor: 'rgba(211, 82, 48, 0.04)',
                },
              }}
              startIcon={
                <img src={PowerPointIcon} alt="PowerPoint" style={{ width: 28, height: 28 }} />
              }
              endIcon={
                isGeneratingPresentation ? (
                  <CircularProgress size={20} sx={{ color: '#D35230' }} />
                ) : (
                  <FileDownloadOutlined sx={{ color: '#D35230' }} />
                )
              }
            />
          )}
        </div>

        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          {responses &&
            responses.some(
              (response) => response.type === AnalysisQuestionType.QUESTION_AND_ANSWER
            ) &&
            processThisPageButton}
        </div>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          {responses &&
            responses.some((response) => response.type === AnalysisQuestionType.KPI) && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#1979CD',
                  cursor: 'pointer',
                  marginBottom: '16px',
                  alignSelf: 'flex-end',
                }}
                onClick={toggleCollapseExpandAllKpis}
              >
                {expandAllKpis ? 'Collapse all' : 'Expand all'}
              </Typography>
            )}
        </div>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          {responses &&
            responses.some((response) => response.type === AnalysisQuestionType.RUBRIC) && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#1979CD',
                  cursor: 'pointer',
                  marginBottom: '16px',
                  alignSelf: 'flex-end',
                }}
                onClick={toggleCollapseExpandAllRubric}
              >
                {expandAllRubric ? 'Collapse all' : 'Expand all'}
              </Typography>
            )}
        </div>
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {responses &&
              page.name !== 'Rubric' &&
              responses.map((response, index) => {
                if (
                  response.type === AnalysisQuestionType.QUESTION_AND_ANSWER &&
                  response.updatedReason !== UpdatedReason.INITIAL
                ) {
                  return (
                    <Section key={`${response.pageName}-${response.promptType}-${index}`}>
                      <QuestionAnswer
                        projectId={project.id}
                        index={index}
                        responseObject={response}
                        versions={allVersions.filter(
                          (v) =>
                            v.promptType === response.promptType &&
                            v.sequence === response.sequence &&
                            v.displayOrder === response.displayOrder
                        )}
                        setIsLoadingThis={setIsLoadingThis}
                        setIsLoadingApplyChanges={setIsLoadingApplyChanges}
                        refetchQuestions={refetchQuestions}
                        handleDelete={handleDeleteQuestion}
                        suggestSaveOrProcessAll={suggestSaveOrProcessAll}
                        setSuggestSaveOrProcessAll={setSuggestSaveOrProcessAll}
                        handleFeedbackSubmit={handleFeedbackSubmit}
                        handleValidateFindingsClick={handleValidateFindingsClick}
                      />
                    </Section>
                  );
                } else if (response.type === AnalysisQuestionType.PROMPT_ANSWER) {
                  return (
                    <Section key={`${response.pageName}-${response.promptType}-${index}`}>
                      <PromptAnswer
                        projectId={project.id}
                        versions={allVersions.filter(
                          (v) =>
                            v.promptType === response.promptType &&
                            v.sequence === response.sequence &&
                            v.displayOrder === response.displayOrder
                        )}
                        textfieldName={`PA-response-${index}`}
                        setIsLoadingThis={setIsLoadingThis}
                        setIsLoadingApplyChanges={setIsLoadingApplyChanges}
                        refetchQuestions={refetchQuestions}
                        timeToProcess={
                          page.name === 'Rubric Summary' ? '5-10 minutes' : '1-2 minutes'
                        }
                        handleFeedbackSubmit={handleFeedbackSubmit}
                        onSelectVersion={(versionId) => handleSelectVersion(response.id, versionId)}
                      />
                    </Section>
                  );
                }
              })}
            {responses?.some(
              (response) => response.type === AnalysisQuestionType.QUESTION_AND_ANSWER
            ) && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                }}
              >
                <Tooltip
                  title={<div style={{ whiteSpace: 'pre-line' }}>{'Add another question'}</div>}
                >
                  <Button variant="outlined" onClick={handleAddQuestion} sx={{ width: '100px' }}>
                    <Add /> Add
                  </Button>
                </Tooltip>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  {processThisPageButton}
                </div>
              </div>
            )}
            {pageQuestions && page.name.includes('KPIs') && (
              <KPIPage
                pageQuestions={pageQuestions}
                refetchQuestions={refetchQuestions}
                setIsLoadingThis={setIsLoadingThis}
                setIsLoadingApplyChanges={setIsLoadingApplyChanges}
                expandAll={expandAllKpis}
                handleFeedbackSubmit={handleFeedbackSubmit}
                handleValidateFindingsClick={handleValidateFindingsClick}
              />
            )}
            {responses && page.name.includes('Rubric') && page.name !== 'Rubric Summary' && (
              <RubricPage
                projectId={project.id}
                pageQuestions={pageQuestions}
                refetchQuestions={refetchQuestions}
                setIsLoadingThis={setIsLoadingThis}
                setIsLoadingApplyChanges={setIsLoadingApplyChanges}
                expandAll={expandAllRubric}
                handleFeedbackSubmit={handleFeedbackSubmit}
                handleValidateFindingsClick={handleValidateFindingsClick}
              />
            )}
            {pageQuestions && page.name === 'Benchmark' && (
              <BenchmarkPage
                scope={page.scope}
                projectId={project.id}
                pageQuestions={pageQuestions ?? []}
                setShouldFetchQuestions={setShouldFetchQuestions}
              />
            )}
          </div>
        </div>
      </StepPageContainer>
    </div>
  );
};

export default StepPage;
